import {
  DisplayIf,
  Field,
  FormLayout,
  HookForm,
  SubmitButton,
} from '@/components_v2/HookForm';
import {
  Button,
  chakra,
  Divider,
  Flex,
  Heading,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Task } from '../../../interfaces';
import {
  ActiveSwitch,
  DateInputs,
  FormularySelect,
  LocalSelect,
  MemberSelect,
  NameInput,
  ProfileSelect,
  TagsSelect,
  TaskAboutSelect,
} from './taskFormInputs';
import { taskSchema, TaskSchema } from './types';
import { useCreateFavoriteMutation } from '@/hooks/Favorites/useCreateFavoriteMutation';
import { useModals } from '@/hooks/ModalManager';
import SaveFavoriteModal from '@/components_v2/modals/SaveFavoriteModal';
import { FeedbackSuccess } from '@/components/Feedback';
import { OriginType } from '../../../interfaces';
import { FiStar } from 'react-icons/fi';
import { useTheme } from '@/hooks/theme';

type TaskFormProps = {
  defaultValues?: Task;
  isLoading?: boolean;
  isFavorite?: boolean;
  onSubmit?: (payload: TaskSchema) => void;
  onDismiss?: () => void;
};


const formatToFavoriteValues = (payload: any): any => {
  const {
    name,
    description,
    associateFormulary,
    formulary,
    local,
    tags,
    inheritChecklistName,
    member,
    user_profile_id,
    origin_id,
    origin_type,
    active,
  } = payload;

  return {
    name,
    description,
    withChecklist: !!associateFormulary,
    inheritFormularyName: !!inheritChecklistName,
    formulary: formulary ? [{ label: formulary.label, value: formulary.value }] : [],
    local: local ? [{ label: local.label, value: local.value }] : [],
    tags: tags?.map((tag: any) => ({
      label: tag.label,
      value: tag.value,
      data: { color: tag.color },
    })) || [],
    member,
    user_profile_id,
    origin_id,
    origin_type,
    active,
  };
};

export function TaskForm(props: TaskFormProps) {
  const toast = useToast();
  const { defaultValues, isLoading, onDismiss, onSubmit, isFavorite: initialIsFavorite } = props;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isFavorite, setIsFavorite] = useState(!!initialIsFavorite);

  const modals = useModals();
  const formRef = useRef<any>(null);
  const { mutateAsync: createFavorite, isLoading: isSavingFavorite } = useCreateFavoriteMutation();

  const formattedDefaultValues:
    | Partial<TaskSchema>
    | undefined = React.useMemo((): Partial<TaskSchema> | undefined => {
    if (!defaultValues) return undefined;

    const fav = defaultValues as any;

    if (initialIsFavorite) {
      const formularyObj = Array.isArray(fav.formulary) ? fav.formulary[0] : fav.formulary;
      const localObj = Array.isArray(fav.local) ? fav.local[0] : fav.local;

      return {
        name: fav.name,
        description: fav.description,
        active: fav.active ?? true,
        associateFormulary: !!fav.withChecklist,
        inheritChecklistName: !!fav.inheritFormularyName,
        formulary: formularyObj,
        local: localObj,
        tags: (Array.isArray(fav.tags) ? fav.tags : []).map((t: any) => ({
          label: t.label || t.name,
          value: t.value || t.id,
          color: t.data?.color ?? t.color,
        })),
        member: fav.member ?? (fav.member_id ? { label: fav.member_name || '', value: fav.member_id } : undefined),
        user_profile_id: fav.user_profile_id ?? fav.user_profile,
        origin_type: Array.isArray(fav.origin_type) ? fav.origin_type[0] : (typeof fav.origin_type === 'string' ? { label: t('registrations.user'), value: fav.origin_type } : fav.origin_type),
        origin_id: Array.isArray(fav.origin_id) ? fav.origin_id[0] : (typeof fav.origin_id === 'string' ? { label: fav.origin_name || '', value: fav.origin_id } : fav.origin_id),
        postponedStart: !!(fav.date_start || fav.dateStart),
        date_start: (fav.date_start || fav.dateStart) ? new Date(fav.date_start || fav.dateStart) : undefined,
        date_end: (fav.date_end || fav.dateEnd) ? new Date(fav.date_end || fav.dateEnd) : undefined,
      };
    }

    const {
      active,
      batch_tags,
      name,
      date_end,
      date_start,
      member_id,
      local_id,
      local_name,
      formulary_id,
      formulary_name,
      member_name,
      description,
      user_profile_id,
      origin_type,
      origin_id,
      origin_name,
    } = fav;

    return {
      name,
      active: active ?? true,
      associateFormulary: !!formulary_id,
      member: member_id
        ? {
            label: member_name || '',
            value: member_id,
          }
        : fav.member, // Fallback to member object if present
      date_end: date_end ? new Date(date_end) : undefined,
      date_start: date_start ? new Date(date_start) : undefined,
      description,
      formulary:
        formulary_id && formulary_name
          ? { value: formulary_id, label: formulary_name }
          : Array.isArray(fav.formulary) ? fav.formulary[0] : fav.formulary,
      local:
        local_id
          ? {
              label: local_name || local_id,
              value: local_id,
            }
          : Array.isArray(fav.local) ? fav.local[0] : fav.local,
      tags:
        batch_tags?.map((t: any) => ({
          label: t.name || t.label,
          value: t.id || t.value,
          color: t.color,
        })) || (Array.isArray(fav.tags) ? fav.tags : []).map((t: any) => ({
          label: t.label || t.name,
          value: t.value || t.id,
          color: t.data?.color ?? t.color,
        })),
      postponedStart: !!date_start,
      user_profile_id,
      origin_type: origin_type
        ? typeof origin_type === 'string'
          ? { label: t('registrations.user'), value: origin_type }
          : origin_type
        : undefined,
      origin_id: origin_id
        ? typeof origin_id === 'string'
          ? {
              label: origin_name || '',
              value: origin_id,
            }
          : origin_id
        : undefined,
    };
  }, [defaultValues, initialIsFavorite, t]);

  const handleSaveFavorite = useCallback(() => {
    const modalId = 'save-task-favorite-modal';
    modals.open({
      id: modalId,
      scope: 'alert',
      title: t('favorites.save_as_favorite', 'Salvar como favorito'),
      body: (
        <SaveFavoriteModal
          onCancel={() => modals.close(modalId)}
          onSave={async (name) => {
            const values = formRef.current?.getValues();
            if (values) {
              const favoriteValues = formatToFavoriteValues(values);
              const member = values.member;
              const accessSettings = {
                users: member?.value ? [member.value] : [],
                teams: [],
              };

              await createFavorite({
                name,
                favorite_type: 'task',
                platform: 'web',
                values: favoriteValues,
                access_settings: accessSettings,
              }, {
                onSuccess: () => {
                  modals.close(modalId);
                  setIsFavorite(true);
                  FeedbackSuccess({
                    mainText: t('favorites.save_success', 'Favorito salvo com sucesso'),
                  });
                },
              });
            }
          }}
          isLoading={isSavingFavorite}
        />
      ),
    });
  }, [createFavorite, isSavingFavorite, modals, t]);

  return (
    <chakra.fieldset
      flex={1}
      overflowY="hidden"
      display="flex"
      disabled={!!isLoading}
    >
      <HookForm<any>
        formRef={formRef}
        onError={(errors: any) => {
          toast({
            title: t('registrations.error'),
            status: 'error',
            position: 'bottom-left',
            description: Object.values(errors)
              .map((e: any) => e?.message)
              .filter(Boolean)
              .join(', \n'),
          });
        }}
        onSubmit={(values) => onSubmit?.(values)}
        resolver={yupResolver(taskSchema)}
        display="flex"
        values={formattedDefaultValues}
        maxH="100%"
        overflowY="hidden"
        flexDir="column"
        w="full"
        justifyContent="space-between"
      >
        <Flex overflowY="auto" borderTopWidth="thin" p="10" flex="1">
          <FormLayout w="full" spacing={8}>
            <FormLayout>
              <Heading size="md">{t('tasks.form.general_datails')}</Heading>
              <NameInput />

              <Field<any>
                type="textarea"
                name="description"
                label={t('tasks.form.description')}
              />

              <TaskAboutSelect />

              <DisplayIf
                name={'origin_type' as any}
                condition={(val: any) => val?.value === 'User'}
              >
                <MemberSelect schemaName="origin_id" />
              </DisplayIf>
            </FormLayout>

            <Divider />

            <FormLayout>
              <Heading size="md">{t('tasks.form.task_checklist')}</Heading>
              <FormularySelect />
            </FormLayout>

            <Divider />

            <FormLayout>
              <Heading size="md">{t('tasks.form.responsable_and_profile')}</Heading>
              <MemberSelect schemaName="member" />
              <ProfileSelect />
            </FormLayout>
            <Divider />
            <FormLayout>
              <Heading size="md">{t('tasks.form.date_and_hour')}</Heading>
              <DateInputs />
            </FormLayout>

            <Divider />

            <FormLayout>
              <Heading size="md">{t('tasks.form.execution_and_tags')}</Heading>
              <LocalSelect />

              <FormLayout mb="20">
                <TagsSelect />
              </FormLayout>
            </FormLayout>
          </FormLayout>
        </Flex>
        <Flex borderTopWidth="thin" p="6" flexDir="column" gap={4}>
          <HStack w="full" justifyContent="space-between">
            <ActiveSwitch />
            <HStack>
              {isFavorite ? (
                <Button 
                  variant="ghost" 
                  colorScheme="gray" 
                  isDisabled
                  leftIcon={<FiStar size={18} color={colors.primary} fill={colors.primary} />}
                >
                  {t('favorites.favorite', 'Favorito salvo')}
                </Button>
              ) : (
                <Button 
                  variant="ghost" 
                  size="md"
                  colorScheme="primary" 
                  onClick={handleSaveFavorite}
                  isDisabled={isSavingFavorite}
                  leftIcon={<FiStar size={18} color={colors.primary} fill="transparent" />}
                >
                  {t('favorites.save_as_favorite', 'Salvar como favorito')}
                </Button>
              )}
              <Button
                colorScheme="red"
                size="md"
                variant="outline"
                onClick={onDismiss}
              >
                {t('common.cancel')}
              </Button>
              <SubmitButton
                isLoading={isLoading}
                size="md"
                colorScheme="primary"
                translate="no"
              >
                {t('common.save')}
              </SubmitButton>
            </HStack>
          </HStack>
        </Flex>
      </HookForm>
    </chakra.fieldset>
  );
}
